import { config } from "../application/common/static-config";

const BLOB_PROXY_PATH = "/api/blob-proxy";

function trimTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, "");
}

function isAbsoluteUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function isDataUrl(value: string): boolean {
  return /^data:/i.test(value);
}

function buildAbsoluteUrl(pathOrUrl: string): string {
  const cleanValue = pathOrUrl.trim();
  if (!cleanValue || isAbsoluteUrl(cleanValue)) return cleanValue;

  const apiBaseUrl = trimTrailingSlashes((config.baseURL ?? "").trim());
  if (!apiBaseUrl) return cleanValue;

  if (cleanValue.startsWith("/")) {
    try {
      const apiOrigin = new URL(apiBaseUrl).origin;
      return `${apiOrigin}${cleanValue}`;
    } catch {
      return `${apiBaseUrl}${cleanValue}`;
    }
  }

  return `${apiBaseUrl}/${cleanValue.replace(/^\/+/, "")}`;
}

function isBlobProxyUrl(value: string): boolean {
  if (!value) return false;

  try {
    const parsed = new URL(value, "http://dummy.local");
    return (
      parsed.pathname.toLowerCase().endsWith(BLOB_PROXY_PATH) &&
      parsed.searchParams.has("path")
    );
  } catch {
    return value.includes(`${BLOB_PROXY_PATH}?path=`);
  }
}

function stripSasTokenFromUrl(url: string): string {
  if (!url) return "";

  if (!isAbsoluteUrl(url)) {
    const queryIndex = url.indexOf("?");
    return queryIndex > 0 ? url.substring(0, queryIndex) : url;
  }

  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    const queryIndex = url.indexOf("?");
    return queryIndex > 0 ? url.substring(0, queryIndex) : url;
  }
}

function normalizeBlobPath(urlOrPath: string): string {
  if (!urlOrPath) return "";

  const cleanValue = urlOrPath.trim();

  if (isDataUrl(cleanValue)) return "";

  if (!isAbsoluteUrl(cleanValue)) {
    return stripSasTokenFromUrl(cleanValue).replace(/^\/+/, "");
  }

  const normalizedStorageUrl = trimTrailingSlashes(
    (config.BLOB_PUBLIC_URL ?? "").trim(),
  );
  const normalizedValue = stripSasTokenFromUrl(cleanValue);

  if (!normalizedStorageUrl) return "";

  if (!normalizedValue.toLowerCase().startsWith(normalizedStorageUrl.toLowerCase())) {
    return "";
  }

  return normalizedValue.substring(normalizedStorageUrl.length).replace(/^\/+/, "");
}

function toBase64Utf8(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary);
}

function fromBase64Utf8(value: string): string {
  const binary = atob(value);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

// Given a blob-proxy URL (e.g. "/api/blob-proxy?path=<base64>"), decode the "path"
// query param back to the underlying blob path (e.g. "TechnicalDrawings/foo-guid.pdf").
// Returns null for anything that isn't a blob-proxy URL.
export function extractBlobPathFromProxyUrl(urlOrPath: string): string | null {
  if (!urlOrPath || !isBlobProxyUrl(urlOrPath)) return null;

  const queryIndex = urlOrPath.indexOf("?");
  if (queryIndex < 0) return null;

  try {
    const pathParam = new URLSearchParams(urlOrPath.slice(queryIndex + 1)).get("path");
    if (!pathParam) return null;
    return fromBase64Utf8(pathParam) || null;
  } catch {
    return null;
  }
}

export function buildBlobProxyUrl(urlOrPath: string): string {
  if (!urlOrPath) return urlOrPath;

  const cleanValue = urlOrPath.trim();

  if (isDataUrl(cleanValue)) return cleanValue;

  if (isBlobProxyUrl(cleanValue)) {
    return buildAbsoluteUrl(cleanValue);
  }

  const blobPath = normalizeBlobPath(cleanValue);
  if (!blobPath) return cleanValue;

  const encodedPath = encodeURIComponent(toBase64Utf8(blobPath));
  const apiBaseUrl = trimTrailingSlashes((config.baseURL ?? "").trim());

  if (!apiBaseUrl) {
    return `${BLOB_PROXY_PATH}?path=${encodedPath}`;
  }

  const blobProxyBase = apiBaseUrl.toLowerCase().endsWith("/api")
    ? `${apiBaseUrl}/blob-proxy`
    : `${apiBaseUrl}${BLOB_PROXY_PATH}`;

  return `${blobProxyBase}?path=${encodedPath}`;
}
